import styled from '@emotion/styled'

import Image from '@segment/matcha/components/Image'
import FeatureBoxBase, * as FeatureBox from '@segment/matcha/components/FeatureBox'
import ColumnTrack from '@segment/matcha/components/ColumnTrack'
import Elevation from '@segment/matcha/components/Elevation'

import CopyTextBase, * as CopyText from '@segment/matcha/recipes/CopyText'
import SectionBase, * as Section from '@segment/matcha/recipes/Section'

import deletionRequestsPng2x from './assets/DeletionRequests@2x.png'
import deletionRequestsWebp from './assets/DeletionRequests.webp'
import deletionRequestsWebp2x from './assets/DeletionRequests@2x.webp'
import deletionRequestsWebp3x from './assets/DeletionRequests@3x.webp'

const copy = {
  heading: `Manage consumer deletion requests at scale`,
}

const features = [
  {
    heading: `Delete user data from Segment`,
    description: `Honor consumer deletion requests with a simple interface or use Segment’s HTTP API to delete a specified user’s data from Segment.`,
  },
  {
    heading: `Forward deletion requests`,
    description: `Automatically delete user data from supported raw data destinations or send deletion requests to compatible tools, like Mixpanel, Braze, and Intercom.`,
  },
  {
    heading: `Monitor deletion status and requests`,
    description: `Get visibility into the progress of deletion requests to confirm when data is deleted, so you can update your consumers and your company.`,
  },
]

const link = {
  text: `View API docs`,
  url: `https://segment.com/docs/privacy/user-deletion-and-suppression/`,
}

const ManagerConsumer = () => {
  return (
    <SectionBase variant="2Col" reverse={true}>
      <SectionCopy>
        <CopyTextBase>
          <CopyText.Heading variant="h2" tag="h2">
            {copy.heading}
          </CopyText.Heading>
          <CopyText.Description tag="div">
            <ColumnTrackWrapper>
              <ColumnTrack columns={1} overrides={{ sm: 3, xl: 1 }} gap="small">
                {features.map((feature) => (
                  <FeatureBoxBase key={feature.heading} alignment="left">
                    <FeatureBox.Body>
                      <FeatureBox.Title>{feature.heading}</FeatureBox.Title>
                      <FeatureBox.Text>{feature.description}</FeatureBox.Text>
                    </FeatureBox.Body>
                  </FeatureBoxBase>
                ))}
              </ColumnTrack>
            </ColumnTrackWrapper>
          </CopyText.Description>
          <Link href={link.url}>{link.text}</Link>
        </CopyTextBase>
      </SectionCopy>
      <SectionContents>
        <Elevation elevation={1} type="dropShadow">
          <picture>
            <source
              type="image/webp"
              srcSet={`${deletionRequestsWebp}, ${deletionRequestsWebp2x} 2x, ${deletionRequestsWebp3x} 3x`}
            />
            <StyledImage
              src={deletionRequestsPng2x}
              alt="Illustration: Manage consumer deletion requests at scale"
              width="540"
              height="528"
              loading="lazy"
            />
          </picture>
        </Elevation>
      </SectionContents>
    </SectionBase>
  )
}

const StyledImage = styled(Image)`
  margin: auto auto;
  padding-left: 50px;
  padding-right: 50px;
`

const SectionCopy = styled(Section.Copy)`
  ${({ theme }) => theme.breakpoints.up(`lg`)} {
    max-width: 392px;
    justify-self: end;
  }
`

const SectionContents = styled(Section.Contents)`
  ${({ theme }) => theme.breakpoints.up(`lg`)} {
    align-self: center;
  }
`

const Link = styled(CopyText.Link)`
  margin-top: 40px;
`

const ColumnTrackWrapper = styled.div`
  margin-top: 40px;
`

export default ManagerConsumer
